import { useEffect, useState } from 'react';

import { get as getUser } from 'js/lib/user';

import { useCourseProgressModuleProgressContext } from 'bundles/course-v2/providers/CourseProgressModuleProgressProvider';
// eslint-disable-next-line import/no-cycle
import {
  hasCompletedFirstModuleInLocalStorage,
  isFreemiumModelRolloutGAEnabled,
  setHasCompletedFirstModuleToLocalStorage,
} from 'bundles/enroll-common/utils/freemiumModelUtils';
import { useNaptime } from 'bundles/naptimejs';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'bund... Remove this comment to see the full error message
import GuidedCourseSessionProgressesV1 from 'bundles/naptimejs/resources/guidedCourseSessionProgresses.v1';

type PropsFromNaptime = {
  guidedCourseSessionProgresses: GuidedCourseSessionProgressesV1;
};

export function useHasCompletedFirstModule({
  courseId,
  shouldReturnNumberOfModules = false,
  skip = false,
}: {
  courseId: string;
  shouldReturnNumberOfModules?: boolean;
  skip?: boolean;
}) {
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const hasCompletedFirstModuleLocalStorageValue = hasCompletedFirstModuleInLocalStorage(courseId);
  const userId = getUser()?.id;
  const skipCondition =
    skip || !courseId || !userId || (hasCompletedFirstModuleLocalStorageValue && !shouldReturnNumberOfModules);
  const isGAEnabled = isFreemiumModelRolloutGAEnabled();

  const {
    moduleProgressData,
    refetch: refreshCourseProgressModuleProgress,
    loading: loadingCourseProgressModuleProgress,
  } = useCourseProgressModuleProgressContext();

  const {
    data: { guidedCourseSessionProgresses },
    pending,
    naptime,
  } = useNaptime<PropsFromNaptime>(() => {
    if (isGAEnabled || skipCondition) return {};

    return {
      guidedCourseSessionProgresses: GuidedCourseSessionProgressesV1.multiGet([`${userId}~${courseId}`], {
        fields: ['weeks'],
      }),
    };
  }, [userId, courseId, skipCondition]);

  const triggerRefresh = () => {
    if (hasCompletedFirstModuleLocalStorageValue) {
      return;
    }

    if (isGAEnabled) {
      refreshCourseProgressModuleProgress?.();
    } else if (naptime) {
      setLoadingRefresh(true);
      naptime.refreshData({ resources: [GuidedCourseSessionProgressesV1.RESOURCE_NAME] }).then(() => {
        setLoadingRefresh(false);
      });
    }
  };

  let isFirstModuleCompleted = false;
  let numberOfModulesInCourse = 0;

  if (isGAEnabled) {
    isFirstModuleCompleted =
      hasCompletedFirstModuleLocalStorageValue ||
      Boolean(
        moduleProgressData?.some((module) => module.moduleIndex === 0 && module.moduleCompletionStatus === 'COMPLETED')
      );
    numberOfModulesInCourse = moduleProgressData?.length ?? 0;
  } else {
    isFirstModuleCompleted =
      hasCompletedFirstModuleLocalStorageValue ||
      Boolean(guidedCourseSessionProgresses?.[0]?.weeks?.[0]?.weekStatus === 'COMPLETED');
    numberOfModulesInCourse = guidedCourseSessionProgresses?.[0]?.weeks?.length ?? 0;
  }

  useEffect(() => {
    if (isFirstModuleCompleted && courseId) {
      setHasCompletedFirstModuleToLocalStorage(courseId);
    }
  }, [isFirstModuleCompleted, courseId]);

  return {
    hasCompletedFirstModule: isFirstModuleCompleted,
    loading: pending || loadingRefresh || loadingCourseProgressModuleProgress,
    triggerRefresh,
    numberOfModulesInCourse,
  };
}
